import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddAutographPicControllerMutation,
  useDeleteAutographPicControllerMutation,
} from "features/api/usersPermissionsManagement/usersPermissionsManagementApiSlice";
import Modal from "react-bootstrap/Modal";
import { Button, Spinner } from "react-bootstrap";
import { XSquareFill } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import {
  ERROR_MESSAGE,
  SUCCESS_MESSAGE,
} from "components/Reports/reportAssets/assets";
import { controllerData } from "./controllerTypes";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: null) => void;
  controller: controllerData;
};

const AutographPicModal = ({ controller, isOpen, setIsOpen }: Props) => {
  const { t } = useTranslation();
  const fileInput = useRef(null);
  const [isImageSelected, setIsImageSelected] = useState(
    controller?.isAutographPictureExist
  );
  const [invalidTypeFile, setInvalidTypeFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addAutographPic] = useAddAutographPicControllerMutation();
  const [deleteAutographPic] = useDeleteAutographPicControllerMutation();

  const handleUploadFile = async (event) => {
    setIsLoading(true);

    const file = event.target.files[0];
    const allowedExtensions = /(\.jpg|\.jpeg|\.png)$/i; // Regular expression to match the desired extensions

    if (!allowedExtensions.exec(file.name)) {
      setIsLoading(false);
      setInvalidTypeFile(true);
      fileInput.current.value = ""; // Reset the input field
      setTimeout(() => {
        setInvalidTypeFile(false);
      }, 2000); // Hide the error message after 2 seconds
    } else {
      const formData = new FormData();
      formData.append("autographPic", file);
      formData.append("controllerId", controller?.id);

      try {
        const res = await addAutographPic(formData).unwrap();
        if (res) {
          toast.success(SUCCESS_MESSAGE);
          setIsOpen(null);
        } else toast.error(ERROR_MESSAGE);
      } catch (error) {
        console.error("Error:", error);
        toast.error(ERROR_MESSAGE);
      }
      setIsLoading(false);
    }
  };

  const handleDeleteImage = async () => {
    setIsLoading(true);

    try {
      const res = await deleteAutographPic(controller?.id).unwrap();
      if (res) {
        toast.success(SUCCESS_MESSAGE);
        setIsImageSelected(false);
      } else toast.error(ERROR_MESSAGE);
    } catch (error) {
      console.error("Error:", error);
      toast.error(ERROR_MESSAGE);
    }
    setIsLoading(false);
  };

  const renderContent = () => {
    if (isImageSelected) {
      return (
        <div className="flexContainer space-between" style={{ width: "30%" }}>
          <span>
            {t("autographPicNameLabel")} {t("autographPic")}
          </span>
          <button type="button" onClick={handleDeleteImage}>
            <XSquareFill width="24" height="24" fill="red" />
          </button>
        </div>
      );
    }
    return (
      <div>
        <Button
          variant="primary"
          style={{ width: "30%" }}
          onClick={() => fileInput.current.click()}
        >
          {t("selectAutographPic")}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".jpg, .jpeg, .png"
          onChange={handleUploadFile}
          style={{ display: "none" }}
        />
        {invalidTypeFile && (
          <div className="requiredField">{t("invalidTypeFile")}</div>
        )}
      </div>
    );
  };

  return (
    <Modal
      size="lg"
      show={isOpen}
      onHide={() => setIsOpen(null)}
      aria-labelledby="contained-modal-title-vcenter"
      contentClassName="form-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("autographPic")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <Spinner animation="border" variant="secondary" />
        ) : (
          renderContent()
        )}
      </Modal.Body>
    </Modal>
  );
};
export default AutographPicModal;
